
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';
import ZccContractStore from '@/store/modules/zccContract';
// @ts-expect-error:
import customerDataService from '@platform/customer-data-service';
import { zccRbacService } from '@/services/zcc-rbac';
import { PlanChangeStatuses } from '@/types/PlanStatus';
import Hint from '@/components/Hint.vue';
import ChannelPlanExceededMixin from '@/mixins/channel-plan-exceeded';
import SessionMixin from '@/mixins/session';
import { IContractZcc } from '@/types/contract-zcc';
import { FinancialOfficer } from '@/types/financial-officer';
import CardSoftwarePlanSummary from './CardSoftwarePlanSummary.vue';
import CardChannelPlanSummary from './CardChannelPlanSummary.vue';

enum StatusBadges {
  ACTIVE = 'is-success',
  BLOCKED = 'is-danger',
  CANCELLED = 'is-danger',
  CHARGE_BACK = 'is-danger',
  CONTRACT_TERMINATION = 'is-danger',
  CHANGED = 'is-warning',
  WAITING_ACTIVATION = 'is-warning',
  WAITING_CANCELLATION = 'is-warning',
  InApprovalProcess = 'is-warning',
  Draft = '',
  Activated = 'is-success',
  Cancelado = 'is-danger',
  Rescindido = 'is-danger',
  Bloqueado = 'is-danger'
}

enum PaymentMethodsBadges {
  BOLETO = 'fa-regular fa-regular fa-file',
  CREDIT_CARD = 'fa-regular fa-credit-card',
  DEPOSIT = 'fa-regular fa-piggy-bank',
  Transferência = 'fa-regular fa-money-bill-transfer',
}

enum BadgeIcons {
  ACTIVE = 'fak fa-success-medium',
  BLOCKED = 'fa-regular fa-circle-minus',
  CANCELLED = 'fa-regular fa-circle-minus',
  CHARGE_BACK = 'fa-regular fa-circle-minus',
  CONTRACT_TERMINATION = 'fa-regular fa-circle-minus',
  CHANGED = 'fak fa-warning-medium',
  WAITING_ACTIVATION = 'fak fa-warning-medium',
  WAITING_CANCELLATION = 'fak fa-warning-medium',
}

const zccContract = namespace('zccContract');

@Component({
  components: {
    CardSoftwarePlanSummary,
    CardChannelPlanSummary,
    Hint,
  },
})

export default class Contracts extends mixins(ChannelPlanExceededMixin, SessionMixin) {
  public statusBadges;

  public paymentMethodBadges;

  public badgeIcons;

  public context: any;

  public contractOwner!: FinancialOfficer;

  canViewUpgradeDowngrade = false;

  canAccessFinances = true;

  canAccessConsumptionReport = true;

  // eslint-disable-next-line
  constructor(vueInstance: any) {
    super();
    this.statusBadges = StatusBadges;
    this.paymentMethodBadges = PaymentMethodsBadges;
    this.badgeIcons = BadgeIcons;
  }

  @Prop({
    required: true,
    default: () => ({}),
    type: Object,
  }) readonly contract!: IContractZcc;

  @Prop({
    required: false,
    default: null,
    type: Function,
  }) readonly onSelected!: (newContract: IContractZcc) => void;

  @Prop({
    required: false,
    default: null,
    type: Boolean,
  }) readonly disableChangePlan!: Boolean;

  @Prop({
    required: false,
    default: null,
    type: String,
  }) readonly planChangeStatus!: String;

  get planChangeStatuses() {
    return PlanChangeStatuses;
  }

  public myWalletUrl = process.env.VUE_APP_MY_WALLET;

  public profile = '';

  @zccContract.Mutation
  public setZccContract!: (contract: ZccContractStore) => void;

  public formatDate = (date: string, locale = navigator.language) => {
    if (!date) {
      return '';
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    };
    return new Date(date).toLocaleDateString(locale, options);
  };

  public contractSelected() {
    if (this.onSelected && typeof this.onSelected === 'function') {
      this.onSelected(this.contract);
    }
  }

  public formatContractName(name: string) {
    const length = 30;
    return name.length > length ? `${name.slice(0, length)}...` : name;
  }

  public getStatusBadgeClass() {
    return this.statusBadges[this.contract.status.replace(/ /g, '') as keyof typeof this.statusBadges];
  }

  public getPaymentMethodIconClass() {
    return this.paymentMethodBadges[this.contract.paymentMethod as keyof typeof this.paymentMethodBadges];
  }

  public getBadgeIconClass() {
    return this.badgeIcons[this.contract.status as keyof typeof this.badgeIcons];
  }

  public changePlanRedirect() {
    const origin = this.$route.query['z-origin'];
    this.$router.push({
      path: '/plans-and-contracts/change-plan',
      query: origin ? { 'z-origin': origin } : undefined,
    });
  }

  public goToConsumptionPage() {
    window.open('/analysis/consumption', '_blank');
  }

  public async getContractFinancialOwner(contract: IContractZcc) {
    this.contractOwner = await contract?.financialOfficers?.find((officer) => officer?.roleTypes?.includes('FINANCIAL'));
  }

  public get activeHints() {
    if (this.hasChannelPlanExceeded && this.hasUsersLimitExceeded) {
      return [
        {
          type: 'danger',
          title: this.$t('INVOICES_ZCC.CARD_CONTRACT.EXCEEDED_USERS_AND_PLAN_HINT.TITLE'),
          description: this.$t('INVOICES_ZCC.CARD_CONTRACT.EXCEEDED_USERS_AND_PLAN_HINT.DESCRIPTION'),
        },
      ];
    }

    const hints = [];

    if (this.hasUsersLimitExceeded) {
      hints.push({
        type: 'danger',
        title: this.$t('INVOICES_ZCC.CARD_CONTRACT.EXCEEDED_USERS_HINT.TITLE'),
        description: this.$t('INVOICES_ZCC.CARD_CONTRACT.EXCEEDED_USERS_HINT.DESCRIPTION'),
      });

      return hints;
    }

    if (this.hasUsersLimitReached) {
      hints.push({
        type: 'warning',
        title: this.$t('INVOICES_ZCC.CARD_CONTRACT.USERS_LIMIT_REACHED_HINT.TITLE'),
        description: this.$t('INVOICES_ZCC.CARD_CONTRACT.USERS_LIMIT_REACHED_HINT.DESCRIPTION'),
      });
    }

    if (this.hasChannelPlanExceeded) {
      hints.push({
        type: 'warning',
        title: this.$t('INVOICES_ZCC.CARD_CONTRACT.EXCEEDED_PLAN_HINT.TITLE'),
        description: this.$t('INVOICES_ZCC.CARD_CONTRACT.EXCEEDED_PLAN_HINT.DESCRIPTION'),
      });
    }

    return hints;
  }

  async mounted() {
    this.context = await customerDataService.getContext();
    await this.getContractFinancialOwner(this.contract);
    await this.fetchGeneralConsumption();
    await this.resolveRbacPermissions();
    this.$forceUpdate();
    this.profile = this.mxSession_profile.id;
  }

  private async resolveRbacPermissions() {
    if (!(await zccRbacService.isRbacEnabled())) {
      return;
    }

    this.canAccessFinances = await zccRbacService.canExecute('cfg', 'fin');
    this.canAccessConsumptionReport = await zccRbacService.canExecute('anly', 'cons');
  }

  private isAdmin() {
    return this.profile === '1' || this.profile === '2';
  }
}
